import React, { useState, useRef } from 'react';
import { Propeller, FilterState } from '../types';
import * as XLSX from 'xlsx';
import { Button, ButtonDivider, Toolbar, ToolbarGroup } from './ButtonGroup';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface PropellerDatabaseTabProps {
  propellers: Propeller[];
  onPropellersChange: (propellers: Propeller[]) => void;
  showStatus: (message: string, type?: 'success' | 'warning' | 'error') => void;
}

const PropellerDatabaseTab: React.FC<PropellerDatabaseTabProps> = ({ propellers, onPropellersChange, showStatus }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  
  // 通用的无边框输入框样式
  const seamlessInputStyle = {
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    color: colors.text,
    fontSize: '14px',
    width: 'auto',
    minWidth: '20px',
    padding: '2px',
  };

  // 计算输入框宽度的函数
  const calculateInputWidth = (value: string | number) => {
    const str = String(value);
    // 基础宽度 + 每个字符的宽度（大约10px）+ 更多padding
    const baseWidth = 30;
    const charWidth = 10;
    const padding = 24;
    return Math.max(baseWidth, str.length * charWidth + padding);
  };
  const [filters, setFilters] = useState<FilterState>({
    brand: '',
    diameterRange: '',
    bladesFilter: '',
    materialFilter: ''
  });
  const [selectedPropellers, setSelectedPropellers] = useState<Set<string>>(new Set());
  const [filteredPropellers, setFilteredPropellers] = useState<Propeller[]>(propellers);
  const [editingCell, setEditingCell] = useState<{ propellerId: string; field: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 应用筛选条件
  React.useEffect(() => {
    let filtered = [...propellers];

    if (filters.brand) {
      filtered = filtered.filter(prop => prop.brand === filters.brand);
    }

    if (filters.diameterRange) {
      const [min, max] = filters.diameterRange.split('-').map(Number);
      if (max) {
        filtered = filtered.filter(prop => prop.diameter >= min && prop.diameter <= max);
      } else {
        filtered = filtered.filter(prop => prop.diameter >= min);
      }
    }

    if (filters.bladesFilter) {
      filtered = filtered.filter(prop => prop.blades === parseInt(filters.bladesFilter!));
    }

    if (filters.materialFilter) {
      filtered = filtered.filter(prop => prop.material === filters.materialFilter);
    }

    setFilteredPropellers(filtered);
  }, [propellers, filters]);

  const handleFilterChange = (field: keyof FilterState, value: string | number | undefined) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      brand: '',
      diameterRange: '',
      bladesFilter: '',
      materialFilter: ''
    });
    showStatus(t('propeller.messages.filterCleared'));
  };

  const addPropeller = () => {
    const newPropeller: Propeller = {
      id: Date.now().toString(),
      brand: '',
      spec: '',
      diameter: 0,
      pitch: 0,
      blades: 2,
      weight: 0,
      material: '',
      thrustCoeff: 0,
      powerCoeff: 0,
      efficiency: 0,
      price: 0,
      notes: ''
    };
    onPropellersChange([...propellers, newPropeller]);
    showStatus(t('propeller.messages.propellerAdded'));
  };

  const deleteSelectedPropellers = () => {
    const remainingPropellers = propellers.filter(prop => !selectedPropellers.has(prop.id!));
    onPropellersChange(remainingPropellers);
    setSelectedPropellers(new Set());
    showStatus(t('propeller.messages.propellersDeleted', { count: selectedPropellers.size }));
  };

  const selectAllPropellers = (checked: boolean) => {
    if (checked) {
      setSelectedPropellers(new Set([...filteredPropellers.map(prop => prop.id!)]));
    } else {
      setSelectedPropellers(new Set());
    }
  };

  const selectPropeller = (propellerId: string, checked: boolean) => {
    const newSelected = new Set(selectedPropellers);
    if (checked) {
      newSelected.add(propellerId);
    } else {
      newSelected.delete(propellerId);
    }
    setSelectedPropellers(newSelected);
  };

  const updatePropeller = (propellerId: string, field: keyof Propeller, value: string | number) => {
    const updatedPropellers = propellers.map(prop => {
      if (prop.id === propellerId) {
        return { ...prop, [field]: value };
      }
      return prop;
    });
    onPropellersChange(updatedPropellers);
  };

  // 切换桨叶的分析状态
  const toggleAnalysisStatus = (propellerId: string) => {
    const updatedPropellers = propellers.map(propeller => {
      if (propeller.id === propellerId) {
        return { ...propeller, includeInAnalysis: !propeller.includeInAnalysis };
      }
      return propeller;
    });
    onPropellersChange(updatedPropellers);
    
    const propeller = propellers.find(p => p.id === propellerId);
    const newStatus = !propeller?.includeInAnalysis;
    showStatus(t(newStatus ? 'propeller.messages.addedToAnalysis' : 'propeller.messages.removedFromAnalysis', { 
      name: `${propeller?.brand} ${propeller?.spec}` 
    }));
  };

  // 批量添加到分析
  const addSelectedToAnalysis = () => {
    const updatedPropellers = propellers.map(propeller => {
      if (selectedPropellers.has(propeller.id!)) {
        return { ...propeller, includeInAnalysis: true };
      }
      return propeller;
    });
    onPropellersChange(updatedPropellers);
    showStatus(t('propeller.messages.batchAddedToAnalysis', { count: selectedPropellers.size }));
  };

  // 批量从分析中移除
  const removeSelectedFromAnalysis = () => {
    const updatedPropellers = propellers.map(propeller => {
      if (selectedPropellers.has(propeller.id!)) {
        return { ...propeller, includeInAnalysis: false };
      }
      return propeller;
    });
    onPropellersChange(updatedPropellers);
    showStatus(t('propeller.messages.batchRemovedFromAnalysis', { count: selectedPropellers.size }));
  };

  // 统计参与分析的桨叶数量
  const analysisPropellersCount = propellers.filter(propeller => propeller.includeInAnalysis).length;

  const exportToExcel = () => {
    const headers = [
      t('propeller.headers.brand'),
      t('propeller.headers.spec'),
      t('propeller.headers.diameter'),
      t('propeller.headers.pitch'),
      t('propeller.headers.blades'),
      t('propeller.headers.weight'),
      t('propeller.headers.material'),
      t('propeller.thrustCoeff'),
      t('propeller.powerCoeff'),
      t('propeller.efficiency'),
      t('propeller.headers.price'),
      t('propeller.headers.notes')
    ];
    const data = [headers];

    propellers.forEach(prop => {
      data.push([
        prop.brand,
        prop.spec,
        prop.diameter.toString(),
        prop.pitch.toString(),
        prop.blades.toString(),
        prop.weight.toString(),
        prop.material,
        prop.thrustCoeff.toString(),
        prop.powerCoeff.toString(),
        prop.efficiency.toString(),
        prop.price.toString(),
        prop.notes
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    
    ws['!cols'] = [
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, 
      { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, 
      { wch: 10 }, { wch: 8 }, { wch: 8 }, { wch: 20 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, t('propeller.sheetName'));
    XLSX.writeFile(wb, `${t('propeller.exportFileName')}_${new Date().toISOString().split('T')[0]}.xlsx`);
    showStatus(t('propeller.messages.exported', { count: propellers.length }));
  };

  const importFromExcel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

        const expectedHeaders = [
          t('propeller.headers.brand'),
          t('propeller.headers.spec'),
          t('propeller.headers.diameter'),
          t('propeller.headers.pitch'),
          t('propeller.headers.blades'),
          t('propeller.headers.weight'),
          t('propeller.headers.material'),
          t('propeller.thrustCoeff'),
          t('propeller.powerCoeff'),
          t('propeller.efficiency'),
          t('propeller.headers.price'),
          t('propeller.headers.notes')
        ];
        const headers = jsonData[0];

        if (!validateHeaders(headers, expectedHeaders)) {
          throw new Error(t('propeller.messages.incorrectFormat'));
        }

        const newPropellers: Propeller[] = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row[0] && row[1]) {
            newPropellers.push({
              id: (Date.now() + i).toString(),
              brand: String(row[0] || ''),
              spec: String(row[1] || ''),
              diameter: parseFloat(String(row[2])) || 0,
              pitch: parseFloat(String(row[3])) || 0,
              blades: parseInt(String(row[4])) || 2,
              weight: parseFloat(String(row[5])) || 0,
              material: String(row[6] || ''),
              thrustCoeff: parseFloat(String(row[7])) || 0,
              powerCoeff: parseFloat(String(row[8])) || 0,
              efficiency: parseFloat(String(row[9])) || 0,
              price: parseFloat(String(row[10])) || 0,
              notes: String(row[11] || '')
            });
          }
        }

        if (newPropellers.length > 0) {
          onPropellersChange(newPropellers);
          showStatus(t('propeller.messages.imported', { count: newPropellers.length }));
        } else {
                      throw new Error(t('propeller.messages.noValidData'));
        }
      } catch (error) {
        showStatus(t('propeller.messages.importError', { error: (error as Error).message }), 'error');
      }
    };
    reader.readAsArrayBuffer(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateHeaders = (actualHeaders: any[], expectedHeaders: string[]): boolean => {
    if (!actualHeaders || actualHeaders.length < expectedHeaders.length) {
      return false;
    }
    
    for (let i = 0; i < expectedHeaders.length; i++) {
      if (actualHeaders[i] !== expectedHeaders[i]) {
        return false;
      }
    }
    return true;
  };

  const loadDefaultPropellers = () => {
    import('../data/defaultData').then(({ defaultPropellers }) => {
      onPropellersChange(defaultPropellers);
      showStatus(t('propeller.messages.resetToDefault'));
    });
  };

  const getBrandOptions = () => {
    const brands = [...new Set(propellers.map(prop => prop.brand))].filter(Boolean);
    return brands;
  };

  const getMaterialOptions = () => {
    const materials = [...new Set(propellers.map(prop => prop.material))].filter(Boolean);
    return materials;
  };

  // 渲染可编辑单元格的通用函数
  const renderEditableCell = (propeller: Propeller, field: keyof Propeller, type: string = 'text', step?: string) => {
    const isEditing = editingCell?.propellerId === propeller.id && editingCell?.field === field;
    const value = propeller[field] as string | number;
    
    return (
      <td 
        className="px-4 py-3 cursor-pointer"
        style={{ 
          color: colors.text,
          fontSize: '14px',
          fontWeight: '400',
        }}
        onClick={() => setEditingCell({ propellerId: propeller.id!, field })}
      >
        {isEditing ? (
          <input
            type={type}
            step={step}
            value={value}
            onChange={(e) => {
              const newValue = type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value;
              updatePropeller(propeller.id!, field, newValue);
            }}
            onBlur={() => setEditingCell(null)}
            onKeyDown={(e) => e.key === 'Enter' && setEditingCell(null)}
            className="px-2 py-1"
            style={{
              ...seamlessInputStyle,
              width: `${calculateInputWidth(value)}px`,
            }}
            aria-label={`${t('common.edit')} ${field}`}
            autoFocus
          />
        ) : (
          <span>{value}</span>
        )}
      </td>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 
          className="text-xl font-semibold mb-6"
          style={{ 
            color: colors.text,
            fontSize: '20px',
            fontWeight: '600',
            letterSpacing: '-0.025em'
          }}
        >
          {t('propeller.title')}
        </h2>

        {/* 筛选栏 */}
        <div 
          className="rounded-lg p-4 mb-6"
          style={{ 
            backgroundColor: colors.surface,
            border: `1px solid ${colors.borderLight}`
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label 
                className="block text-xs font-medium mb-2"
                style={{ 
                  color: colors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '11px',
                  fontWeight: '500'
                }}
              >
                {t('propeller.brandFilter')}
              </label>
              <select
                value={filters.brand}
                onChange={(e) => handleFilterChange('brand', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                aria-label={t('propeller.brandFilter')}
              >
                <option value="">{t('propeller.filter.all')}</option>
                {getBrandOptions().map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>

            <div>
              <label 
                className="block text-xs font-medium mb-2"
                style={{ 
                  color: colors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '11px',
                  fontWeight: '500'
                }}
              >
                {t('propeller.diameterRangeFilter')}
              </label>
              <select
                value={filters.diameterRange || ''}
                onChange={(e) => handleFilterChange('diameterRange', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                aria-label={t('propeller.diameterRangeFilter')}
              >
                <option value="">{t('propeller.filter.all')}</option>
                <option value="9-11">9-11</option>
                <option value="11-13">11-13</option>
                <option value="13-99">13+</option>
              </select>
            </div>

            <div>
              <label 
                className="block text-xs font-medium mb-2"
                style={{ 
                  color: colors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '11px',
                  fontWeight: '500'
                }}
              >
                {t('propeller.bladesFilter')}
              </label>
              <select
                value={filters.bladesFilter || ''}
                onChange={(e) => handleFilterChange('bladesFilter', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                aria-label={t('propeller.bladesFilter')}
              >
                <option value="">{t('propeller.filter.all')}</option>
                <option value="2">{t('propeller.twoBlades')}</option>
                <option value="3">{t('propeller.threeBlades')}</option>
              </select>
            </div>

            <div>
              <label 
                className="block text-xs font-medium mb-2"
                style={{ 
                  color: colors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '11px',
                  fontWeight: '500'
                }}
              >
                {t('propeller.materialFilter')}
              </label>
              <select
                value={filters.materialFilter || ''}
                onChange={(e) => handleFilterChange('materialFilter', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                aria-label={t('propeller.materialFilter')}
              >
                <option value="">{t('propeller.filter.all')}</option>
                {getMaterialOptions().map(material => (
                  <option key={material} value={material}>{material}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 工具栏 */}
        <Toolbar>
          <ToolbarGroup title={t('propeller.dataManagement')}>
            <Button onClick={addPropeller} variant="primary">
              {t('propeller.addPropeller')}
            </Button>
            <Button
              onClick={deleteSelectedPropellers}
              disabled={selectedPropellers.size === 0}
              variant="danger"
            >
              {t('propeller.deleteSelected')}
            </Button>
            <Button onClick={loadDefaultPropellers} variant="secondary">
              {t('propeller.resetToDefault')}
            </Button>
          </ToolbarGroup>
          
          <ButtonDivider />
          
          <ToolbarGroup title={t('propeller.filterTitle')}>
            <Button onClick={clearFilters} variant="secondary">
              {t('propeller.clearFilters')}
            </Button>
          </ToolbarGroup>
          
          <ButtonDivider />
          
          <ToolbarGroup title={t('propeller.importExport')}>
            <Button onClick={exportToExcel} variant="success">
              {t('propeller.exportPropellers')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={importFromExcel}
              className="hidden"
              aria-label={t('propeller.importExcelFile')}
            />
            <Button onClick={() => fileInputRef.current?.click()} variant="secondary">
              {t('propeller.importFromExcel')}
            </Button>
          </ToolbarGroup>
          
          <ButtonDivider />
          
          <ToolbarGroup title={t('propeller.analysisManagement')}>
            <Button
              onClick={addSelectedToAnalysis}
              disabled={selectedPropellers.size === 0}
              variant="success"
            >
              {t('propeller.addToAnalysis')} ({selectedPropellers.size})
            </Button>
            <Button
              onClick={removeSelectedFromAnalysis}
              disabled={selectedPropellers.size === 0}
              variant="warning"
            >
              {t('propeller.removeFromAnalysis')} ({selectedPropellers.size})
            </Button>
          </ToolbarGroup>
        </Toolbar>

        {/* 数据表格 */}
        <div 
          className="rounded-lg overflow-hidden"
          style={{ 
            backgroundColor: colors.background,
            border: 'none'
          }}
        >
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead 
                className="sticky top-0 z-10"
                style={{ 
                  backgroundColor: colors.background,
                  borderBottom: `1px solid ${colors.border}`
                }}
              >
                <tr>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={filteredPropellers.length > 0 && selectedPropellers.size === filteredPropellers.length}
                      onChange={(e) => selectAllPropellers(e.target.checked)}
                      aria-label={t('propeller.selectDeselectAll')}
                    />
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('propeller.headers.brand')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('propeller.headers.spec')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('propeller.headers.diameter')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('propeller.headers.pitch')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('propeller.headers.blades')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('propeller.headers.weight')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('propeller.headers.material')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('propeller.thrustCoeff')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('propeller.powerCoeff')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('propeller.headers.efficiency')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('propeller.headers.price')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('propeller.headers.notes')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('propeller.analysisStatus')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredPropellers.map((propeller) => (
                  <tr 
                    key={propeller.id} 
                    className="transition-colors duration-200"
                    style={{
                      backgroundColor: propeller.includeInAnalysis 
                        ? (colors.success + '10') 
                        : 'transparent',
                      borderBottom: `1px solid ${colors.borderLight}`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.surface;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = propeller.includeInAnalysis 
                        ? (colors.success + '10') 
                        : 'transparent';
                    }}
                  >
                    <td 
                      className="px-4 py-3"
                      style={{
                        fontSize: '14px',
                        fontWeight: '400',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPropellers.has(propeller.id!)}
                        onChange={(e) => selectPropeller(propeller.id!, e.target.checked)}
                        aria-label={`${t('propeller.selectPropeller')} ${propeller.brand} ${propeller.spec}`}
                      />
                    </td>
                    {renderEditableCell(propeller, 'brand')}
                    {renderEditableCell(propeller, 'spec')}
                    {renderEditableCell(propeller, 'diameter', 'number', '0.1')}
                    {renderEditableCell(propeller, 'pitch', 'number', '0.1')}
                    {renderEditableCell(propeller, 'blades', 'number')}
                    {renderEditableCell(propeller, 'weight', 'number', '0.1')}
                    {renderEditableCell(propeller, 'material')}
                    {renderEditableCell(propeller, 'thrustCoeff', 'number', '0.0001')}
                    {renderEditableCell(propeller, 'powerCoeff', 'number', '0.0001')}
                    {renderEditableCell(propeller, 'efficiency', 'number')}
                    {renderEditableCell(propeller, 'price', 'number')}
                    {renderEditableCell(propeller, 'notes')}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleAnalysisStatus(propeller.id!)}
                        className="px-3 py-1 text-xs font-medium rounded-full transition-colors"
                        style={{
                          backgroundColor: propeller.includeInAnalysis 
                            ? (colors.success + '20') 
                            : (colors.textMuted + '20'),
                          color: propeller.includeInAnalysis 
                            ? colors.success 
                            : colors.textMuted,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = propeller.includeInAnalysis 
                            ? (colors.success + '30') 
                            : (colors.textMuted + '30');
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = propeller.includeInAnalysis 
                            ? (colors.success + '20') 
                            : (colors.textMuted + '20');
                        }}
                      >
                        {propeller.includeInAnalysis ? t('propeller.included') : t('propeller.notIncluded')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div 
          className="mt-6 text-sm flex items-center gap-4"
          style={{ 
            color: colors.textSecondary,
            fontSize: '13px',
            fontWeight: '400'
          }}
        >
          <span>
            {t('propeller.filterResults', { 
              showing: filteredPropellers.length, 
              total: propellers.length 
            })}
          </span>
          {selectedPropellers.size > 0 && (
            <span>
              {t('propeller.selected')} <span style={{ color: colors.text, fontWeight: '500' }}>{selectedPropellers.size}</span> {t('propeller.count')}
            </span>
          )}
          <span>
            {t('propeller.inAnalysis')}：<span style={{ color: colors.success, fontWeight: '500' }}>{analysisPropellersCount}</span> {t('propeller.count')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PropellerDatabaseTab; 